import {create} from 'zustand';
import {persist} from 'zustand/middleware';

/**
 * Builds a zustand store that lazily fetches a static JSON file (served
 * from /public) and caches the result both in memory and in localStorage.
 * Every mlComponent page (ML, PyTorch, TensorFlow, Scikit, SciPy, LLM)
 * uses one of these instead of hand-rolling its own fetch/cache logic.
 *
 * Caching behaviour:
 *  - `isLoaded` short-circuits `loadData()` once the payload is in the
 *    store, so remounting the page (e.g. navigating between slugs) never
 *    re-fetches.
 *  - `loadPromise` (module-scoped, not store state) collapses concurrent
 *    calls into a single in-flight request, in case more than one
 *    component calls `loadData()` before the first fetch resolves.
 *  - `persist` writes the same data to localStorage under `persistName`,
 *    so a full page reload rehydrates `isLoaded: true` synchronously and
 *    skips the network fetch entirely.
 *
 * @param {string} url - path to the JSON file to fetch, e.g. '/data/pytorchData.json'
 * @param {string} persistName - localStorage key used to persist the cache
 * @param {string[]} keys - top-level array fields expected in the JSON
 *   payload (e.g. ['slugs', 'nextPage', 'relatedPages', 'suggestedCourses']);
 *   each becomes a field on the store, defaulting to `[]` until loaded
 * @returns {Function} a zustand hook, e.g. `useMlDataStore`
 */
export function createDataStore(url, persistName, keys) {
    let loadPromise = null;

    return create(
        persist(
            (set, get) => ({
                ...Object.fromEntries(keys.map(key => [key, []])),
                isLoading: false,
                isLoaded: false,
                error: null,

                loadData: () => {
                    if (get().isLoaded) return Promise.resolve();
                    if (loadPromise) return loadPromise;

                    set({isLoading: true, error: null});

                    loadPromise = fetch(url)
                        .then(res => res.json())
                        .then(data => {
                            const fields = Object.fromEntries(keys.map(key => [key, data[key] ?? []]));
                            set({...fields, isLoading: false, isLoaded: true});
                        })
                        .catch(err => {
                            console.error(`Failed to load data from ${url}`, err);
                            set({isLoading: false, error: err.message});
                        })
                        .finally(() => {
                            loadPromise = null;
                        });

                    return loadPromise;
                }
            }),
            {
                name: persistName,
                // Only persist the fetched data + isLoaded flag, not the
                // transient isLoading/error state.
                partialize: state => ({
                    ...Object.fromEntries(keys.map(key => [key, state[key]])),
                    isLoaded: state.isLoaded
                })
            }
        )
    );
}
